import rdiff from 'recursive-diff'
import Fuse from 'fuse.js'
import {
    ProductRetailer,
    PromotedProductDTO,
    UnformattedPromotedProduct,
} from '../entities'

/**
 * Takes an old and new state of a promoted product object and replaces old values with new values
 * @param currentProduct the current version of the product
 * @param latestProduct the new version of the product
 * @returns the updated promoted product
 */
export function updateProduct(
    currentProduct: UnformattedPromotedProduct,
    latestProduct: UnformattedPromotedProduct
): UnformattedPromotedProduct {
    if (currentProduct.id !== latestProduct.id) {
        return currentProduct
    }

    const diff: rdiff.rdiffResult[] = rdiff.getDiff(
        currentProduct,
        latestProduct
    )
    const updatedProduct: UnformattedPromotedProduct = rdiff.applyDiff(
        currentProduct,
        diff
    )

    return updatedProduct
}

export function searchPromotedProducts(
    products: PromotedProductDTO[],
    query: string
): { products: PromotedProductDTO[] } {
    const options = {
        keys: [
            { name: 'name', weight: 4 },
            { name: 'image.alt', weight: 3 },
            { name: 'retailer.name', weight: 2 },
            { name: 'stages', weight: 1 },
        ],
        threshold: 0.3,
        distance: 100,
        ignoreLocation: true,
    }
    if (query.length === 0) return { products }
    const fuse = new Fuse(products, options)

    const filteredProducts = fuse.search(query).map((result) => result.item)

    const sortedProducts = filteredProducts.sort((a, b) => {
        const aExactMatch = a.name.toLowerCase().includes(query.toLowerCase())
        const bExactMatch = b.name.toLowerCase().includes(query.toLowerCase())

        if (aExactMatch && !bExactMatch) return -1
        if (!aExactMatch && bExactMatch) return 1
        return 0
    })

    return { products: sortedProducts }
}

/**
 * Takes an old and new state of a product retailer object and replaces old values with new values
 * @param currentRetailer the current version of the product
 * @param latestRetailer the new version of the product
 * @returns the updated promoted product
 */
export function updateProductRetailer(
    currentRetailer: ProductRetailer,
    latestRetailer: ProductRetailer
): ProductRetailer {
    if (currentRetailer.id !== latestRetailer.id) {
        return currentRetailer
    }

    const diff: rdiff.rdiffResult[] = rdiff.getDiff(
        currentRetailer,
        latestRetailer
    )
    const updatedProduct: UnformattedPromotedProduct = rdiff.applyDiff(
        currentRetailer,
        diff
    )

    return updatedProduct
}
